// ─── Types ────────────────────────────────────────────────────────────────────

/** Structured representation of a single refundation entry. */
export interface RefundEntry {
  /** "Refundacja we wszystkich zarejestrowanych wskazaniach." when truthy */
  allIndications: boolean;
  diseaseTitle: string | null;
  /** Wskazanie rozbite na bloki przy zrzucie bazy w RBL; null gdy brak struktury */
  diseaseContentBlocks: ContentBlock[] | null;
  comment: string | null;
  /** Numeric co-payment amount (only for "B" refund type when not "bezpł.") */
  copayment: string | null;
}

/** All parsed entries for one refund name (e.g. "100%", "B", …). */
export interface RefundData {
  /** Raw price label, e.g. "bezpł.", "3,20" */
  priceLabel: string;
  entries: RefundEntry[];
}

// ─── Public API ───────────────────────────────────────────────────────────────

/**
 * "3,20" → "3,20 zł"; napisy typu "bezpł." zostają bez zmian.
 */
export function formatPriceLabel(price: string | number | null | undefined) {
  const label = String(price ?? "").trim();
  return /^\d+(?:[.,]\d+)?$/.test(label) ? `${label} zł` : label;
}

/**
 * Returns structured refund data for a given refund name, or `null` when the
 * variant has no price entry with that name.
 */
export function getRefundData(
  prices: MedPriceVM[],
  refundName: string,
): RefundData | null {
  const matching = prices.filter((p) => p.name === refundName);
  if (!matching.length) return null;

  const priceLabel = formatPriceLabel(matching[0].price);

  const entries: RefundEntry[] = matching.map((p) => ({
    allIndications: Boolean(p.indicate),
    diseaseTitle: p.disease?.title ?? null,
    // Pusta tablica jest truthy, a PHP zwraca [] dla grup uprawnionych i dopóki
    // zrzut bazy nie wypełni kolumny — bez normalizacji widok wybrałby gałąź bloków
    // i tytuł by zniknął.
    diseaseContentBlocks: p.disease?.contentBlocks?.length
      ? p.disease.contentBlocks
      : null,
    comment: p.comment ?? null,
    copayment:
      p.price && p.price !== "bezpł." && p.name === "B"
        ? String(p.price)
        : null,
  }));

  return { priceLabel, entries };
}

// ─── Legacy HTML tooltip builder (kept for backward-compat) ───────────────────

/** @deprecated Prefer `getRefundData` + a React component for rendering. */
export const getRefundTooltip = (
  prices: MedPriceVM[],
  link: string,
): string => {
  let tooltip = "";

  for (const price of prices) {
    const entry: RefundEntry = {
      allIndications: Boolean(price.indicate),
      diseaseTitle: price.disease?.title ?? null,
      diseaseContentBlocks: null,
      comment: price.comment ?? null,
      copayment:
        price.price && price.price !== "bezpł." && price.name === "B"
          ? String(price.price)
          : null,
    };

    const content = renderEntryAsHtml(entry, link);
    if (content) {
      const marginStyle =
        prices.indexOf(price) !== prices.length - 1
          ? `style="margin-bottom: 1rem;"`
          : "";
      tooltip += `<div ${marginStyle}>${content}</div>`;
    }
  }

  return tooltip;
};

// ─── Internal helpers ─────────────────────────────────────────────────────────

const ALL_INDICATIONS_ANCHOR = "#mwqyyza4mwfjzgjm";
const ALL_INDICATIONS_LABEL =
  "Refundacja we wszystkich zarejestrowanych wskazaniach.";

function renderEntryAsHtml(entry: RefundEntry, link: string): string {
  let html = "";
  if (entry.allIndications) {
    html += `<a href="${link}${ALL_INDICATIONS_ANCHOR}" class="text-white">${ALL_INDICATIONS_LABEL}</a><br />`;
  }
  if (entry.diseaseTitle) html += `${entry.diseaseTitle}<br />`;
  if (entry.comment)
    html += `${removeTrailingBr(formatComment(entry.comment))}<br />`;
  if (entry.copayment)
    html += `Wysokość dopłaty świadczeniobiorcy: ${entry.copayment} zł`;
  return removeTrailingBr(html);
}

function formatComment(comment: string): string {
  let first = true;
  return comment.replace(/<(\d+)>/g, (_, n) => {
    if (first) {
      first = false;
      return `${n}.`;
    }
    return `<br />${n}.`;
  });
}

function removeTrailingBr(text: string): string {
  return text.replace(/(<br\s*\/?>)+$/i, "");
}
